import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import { ListingConfigProps } from 'layout/Listing/config';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import PressFilters from 'layout/Listing/variants/press/components/Filters';
import SearchBar from 'layout/Listing/variants/press/components/SearchBar';
import useFilterPress from 'layout/Listing/variants/press/hooks/useFilteredPress';
import usePressFilterSearchParam from 'layout/Listing/variants/press/hooks/usePressFilterSearchParam';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';
import { normalizePublishDate } from 'utils/normalize/normalizePublishDate';
import { parseListingDate } from 'utils/parse/parseDate';

import externalNewsData from 'data/externalNews.json';
import pressData from 'data/press.json';

const PAGE_SIZE = 12;

type PressEntry = { data: BlogResource };

const PressListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<PressEntry>(rawLatestData);

  const pressLocal = toArray<PressEntry>(pressData);

  const externalLocal = toArray<PressEntry>(normalizePublishDate(externalNewsData));

  const fallbackCombined = [...pressLocal, ...externalLocal];

  const pressList: PressEntry[] = hasArrayValues(latestData)
    ? mergeListings(normalizePublishDate(latestData), fallbackCombined)
    : fallbackCombined;

  if (!hasArrayValues(pressList)) {
    console.warn('[PressListing] No items to render – check builderState/listingData and press/external JSONs');
  }

  usePressFilterSearchParam();

  const filteredPress = useFilterPress(pressList).sort((a, b) => {
    const dateA = parseListingDate(a.data?.lastUpdated) || parseListingDate(a.data?.publishDate);
    const dateB = parseListingDate(b.data?.lastUpdated) || parseListingDate(b.data?.publishDate);

    return dateB - dateA;
  });

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredPress,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/press');

  useForceReloadOnBackToResources();

  return (
    <>
      <Section {...sectionProps} darkMode={false}>
        <ScrollFadeSection delay={0.1} containPaint={false}>
          <div id="listing-section" className="flex flex-col gap-8">
            <div className="grid grid-cols-1 justify-center gap-8 sm:grid-cols-3 lg:grid-cols-2">
              <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
                {headingTitle || 'All News & Updates'}
              </h2>
              <PressFilters />
              <SearchBar placeholder={searchPlaceholder} />
            </div>
            <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />

            {totalItems > PAGE_SIZE && (
              <Pagination
                count={totalItems}
                pageSize={PAGE_SIZE}
                setPage={detail => handlePageChange(detail.page)}
                currentPage={currentPage}
              />
            )}
          </div>
        </ScrollFadeSection>
      </Section>
      <nav aria-label="All press articles" className="sr-only">
        {filteredPress
          .filter(post => post.data?.url)
          .map(post => (
            <a key={`seo-${post.data.url}`} href={post.data.url?.endsWith('/') ? post.data.url : `${post.data.url}/`}>
              {post.data.title}
            </a>
          ))}
      </nav>
    </>
  );
};

export default PressListing;
